import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useApolloClient } from '@apollo/client';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import useSuspenseQuery from 'commons/useSuspenseQuery';

import {
  REMOVE_COUPON_FROM_CART,
  VALIDATE_AND_APPLY_COUPON_CODE_TO_CART,
} from 'mocks/mutations';
import { GET_CHECKOUT_SUMMARY, GET_CART } from 'mocks/queries';
import roundNumber from 'commons/roundNumber';
import { get } from 'lodash';
import IconCloseEllipse from 'components/icons/iconCloseEllipse';
import avo from 'analytics/avo';
import useUserCurrentPageType from 'hooks/useUserCurrentPageType';
import { logError } from 'commons/logger';
import { getCheckoutRef } from 'components/consumer/checkout/utils/checkout';
import classNames from 'classnames';
import getZipcode from 'commons/getZipcode';

const AnimateHeight = loadable(() => import('react-animate-height'));

// get valid coupon code case-insensitively
export function getValidCouponCode(appliedCoupons, couponCode) {
  let validCode = null;
  if (appliedCoupons && couponCode) {
    const val = Object.keys(appliedCoupons).find(
      key => key.trim().toLowerCase() === couponCode.trim().toLowerCase()
    );
    if (appliedCoupons[val] && appliedCoupons[val].coupon_code) {
      validCode = appliedCoupons[val].coupon_code;
    }
  }
  return validCode;
}

const ApplyCoupon = ({ onEdit, variant = '' }) => {
  const [editMode, setEditMode] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [eligibleCartItemsCount, setEligibleCartItemsCount] = useState(0);
  const [couponData, setCouponData] = useState({
    error: false,
    errorMessage: {
      title: '',
      subtitle: '',
    },
    success: false,
    successMessage: {
      title: '',
      subtitle: '',
    },
  });

  const [errorMessageIsShown, setErrorMessageIsShown] = useState(false);
  const [successMessageIsShown, setSuccessMessageIsShown] = useState(false);

  const client = useApolloClient();
  const [validateAndApply] = useMutation(
    VALIDATE_AND_APPLY_COUPON_CODE_TO_CART
  );

  const zipCode = getZipcode();
  const checkoutRef = getCheckoutRef();
  const {
    loading: getCheckoutSummaryLoading,
    data: getCheckoutSummaryData,
  } = useSuspenseQuery(GET_CHECKOUT_SUMMARY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      ...(checkoutRef ? { checkoutRef } : {}),
      input: {
        zipcode: zipCode,
      },
    },
  });
  const currentPage = useUserCurrentPageType();

  const showError = (title, subtitle = '') => {
    setCouponData({
      ...couponData,
      error: true,
      errorMessage: {
        title,
        subtitle,
      },
    });

    setErrorMessageIsShown(true);

    setTimeout(() => {
      setErrorMessageIsShown(false);
    }, 3000);

    setTimeout(() => {
      setCouponData({
        ...couponData,
        error: false,
      });
    }, 4000);
  };

  const showSuccess = (appliedCode, count) => {
    setCouponData({
      ...couponData,
      success: true,
      successMessage: {
        title:
          count === 1
            ? `Coupon ${appliedCode} has been applied to the item in your cart`
            : `Coupon ${appliedCode} has been applied to all eligible items in your cart`,
        subtitle: null,
      },
    });

    setSuccessMessageIsShown(true);

    setTimeout(() => {
      setSuccessMessageIsShown(false);
    }, 3000);

    setTimeout(() => {
      setCouponData({
        ...couponData,
        success: false,
      });
    }, 4000);
  };

  const getDiscountAmount = (code, couponsList) =>
    roundNumber(couponsList[code].amount_used);

  const submitCouponCode = async () => {
    if (!couponCode) {
      showError('Please enter coupon code', 'Your coupon code field is empty.');
      setEditMode(false);
      if (onEdit) onEdit(false);
      return;
    }
    avo.couponEntered({ couponName: couponCode, location: currentPage });

    const validateResponse = await validateAndApply({
      variables: {
        coupon_code: couponCode,
      },
    });

    // issue
    const [cartResponse, checkoutSummaryResponse] = await Promise.all([
      client.query({
        query: GET_CART,
        fetchPolicy: 'network-only',
        variables: {
          zipCode: getZipcode(),
          ...(checkoutRef ? { checkoutRef } : {}),
        },
      }),
      client.query({
        query: GET_CHECKOUT_SUMMARY,
        fetchPolicy: 'network-only',
        variables: {
          ...(checkoutRef ? { checkoutRef } : {}),
          input: {
            zipcode: zipCode,
          },
        },
      }),
    ]);

    const { success, errors } = validateResponse.data.validateAndApply;

    const { applied_coupons } =
      checkoutSummaryResponse || checkoutSummaryResponse.data
        ? checkoutSummaryResponse.data.checkoutSummary
        : {};
    if (!applied_coupons?.length && success) {
      // Coupon is valid but not applicable
      showError(
        'Coupon valid but not applicable',
        'It may be expired or not applicable for this order.'
      );

      avo.couponDenied({
        couponName: couponCode,
        reason: 'Valid Coupon but not applicable',
        location: currentPage,
      });
    } else if (applied_coupons?.length > 0 && success) {
      const appliedCoupons = applied_coupons.reduce((acc, cur) => {
        acc[cur.coupon_code] = cur;
        return acc;
      }, {});
      const validCode = getValidCouponCode(appliedCoupons, couponCode);
      if (appliedCoupons[validCode]) {
        const cart = get(cartResponse, 'data.getCart.cart') ?? [];
        const count = cart?.length
          ? cart.filter(p => !!p?.exclude_from_sale)?.length
          : 0;
        setEligibleCartItemsCount(count);
        showSuccess(validCode, count);
        setCouponCode('');
        const discount = getDiscountAmount(validCode, appliedCoupons);
        avo.couponApplied({
          couponName: couponCode,
          discount: parseFloat(discount),
          location: currentPage,
        });
      } else {
        showError(
          'The coupon code could not be applied',
          'It may be expired or not applicable for this order'
        );

        avo.couponDenied({
          couponName: couponCode,
          reason: 'Valid Coupon but not applicable',
          location: currentPage,
        });
      }
    } else {
      const couponError =
        get(errors, '0.code', '') === 'ERR_CAA'
          ? 'Coupon already applied'
          : 'Invalid coupon code';
      showError(
        couponError,
        'Your coupon code is already applied or is invalid.'
      );

      avo.couponDenied({
        couponName: couponCode,
        reason: couponError,
        location: currentPage,
      });
    }
    // if success
    setEditMode(false);
    if (onEdit) onEdit(false);
  };

  const getCouponType = (code, couponList) => {
    if (!couponList || !couponList[code]) return '';

    let couponLabel = '';
    switch (couponList[code].type) {
      case 'reward':
        couponLabel = 'Reward';
        break;
      case 'gift':
        couponLabel = 'Gift Card';
        break;
      default:
        couponLabel = 'Discount';
        break;
    }
    return couponLabel;
  };

  const removeCouponCode = async coupon => {
    const removeResponse = await client.mutate({
      mutation: REMOVE_COUPON_FROM_CART,
      variables: {
        coupon_code: coupon,
      },
    });

    const { success } = removeResponse.data.removeSingleCartAppliedCoupon;

    if (success) {
      avo.couponRemoved({
        couponName: coupon,
        location: currentPage,
      });
      await Promise.all([
        client.query({
          query: GET_CART,
          fetchPolicy: 'network-only',
          variables: {
            zipCode: getZipcode(),
            ...(checkoutRef ? { checkoutRef } : {}),
          },
        }),
        client.query({
          query: GET_CHECKOUT_SUMMARY,
          fetchPolicy: 'network-only',
          variables: {
            ...(checkoutRef ? { checkoutRef } : {}),
            input: {
              zipcode: zipCode,
            },
          },
        }),
      ]);
    }
  };

  let appliedCoupons = {};
  try {
    if (
      !getCheckoutSummaryLoading &&
      getCheckoutSummaryData.checkoutSummary?.applied_coupons?.length > 0
    ) {
      appliedCoupons = getCheckoutSummaryData.checkoutSummary.applied_coupons.reduce(
        (acc, cur) => {
          acc[cur.coupon_code] = cur;
          return acc;
        },
        {}
      );
    }
  } catch (e) {
    logError(e);
  }

  return (
    <div
      className={classNames(
        'mt-6 mx-0 transition-[height] duration-300 ease-out text-xs font-bold w-full cursor-pointer',
        {
          'mb-2': variant !== 'mobileTotalSection',
          'mb-4': variant === 'mobileTotalSection',
        }
      )}
    >
      <AnimateHeight
        duration={500}
        height={successMessageIsShown ? 'auto' : 0}
        animateOpacity
      >
        {couponData.success && (
          <div
            className={classNames(
              'text-xs font-bold bg-brand-lighter p-6 mb-6',
              {
                'text-brand-darker': variant === 'mobileTotalSection',
              }
            )}
          >
            <div className="font-bold mb-4">
              {couponData.successMessage.title}
            </div>

            {!!couponData.successMessage.subtitle?.trim() && (
              <div className="font-normal">
                {couponData.successMessage.subtitle}
              </div>
            )}
          </div>
        )}
      </AnimateHeight>

      <AnimateHeight
        duration={500}
        height={errorMessageIsShown ? 'auto' : 0}
        animateOpacity
      >
        {couponData.error && (
          <div className="text-xs font-bold bg-red-light p-6 text-red-dark mb-6">
            <div className="font-bold mb-4">
              {couponData.errorMessage.title}
            </div>

            <div className="font-normal">
              {couponData.errorMessage.subtitle}
            </div>
          </div>
        )}
      </AnimateHeight>

      {!editMode && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          className="text-xs font-bold text-brand border-0 bg-transparent hover:cursor-pointer"
          onClick={() => {
            setEditMode(true);
            if (onEdit) onEdit(true);
          }}
        >
          {' '}
          Apply gift card or promo code +{' '}
        </span>
      )}

      {editMode && (
        <div className="p-2 flex border border-solid border-gray-light6 rounded-sm">
          <div className="flex flex-col w-[80%]">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="edit-mode-label">Gift Card/ Promo Code</label>
            <input
              className="text-lg font-bold border-0 border-none"
              value={couponCode}
              onChange={e => {
                setCouponCode(e.target.value.trim());
              }}
            />
          </div>
          <button
            type="button"
            className="cursor-pointer bg-none border-0 border-none text-brand flex-grow text-right pr-[.6875rem] font-bold"
            onClick={submitCouponCode}
          >
            Apply
          </button>
        </div>
      )}
      <div
        className={classNames(
          'text-xs flex flex-col mt-4 text-red justify-between',
          {
            'font-bold': variant !== 'mobileTotalSection',
          }
        )}
      >
        {Object.keys(appliedCoupons).map(coupon =>
          appliedCoupons[coupon]?.type !== 'gift_card_rewards' ? (
            <div
              key={coupon}
              className="flex flex-row justify-between mb-[.3rem]"
            >
              <div className="flex min-w-[50%]">
                <button
                  type="button"
                  onClick={() => showSuccess(coupon, eligibleCartItemsCount)}
                >
                  {getCouponType(coupon, appliedCoupons)}:{' '}
                  <span className="underline">{coupon}</span>
                </button>
                <div className="ml-[.4rem] flex items-center">
                  <IconCloseEllipse onClick={() => removeCouponCode(coupon)} />
                </div>
              </div>
              <div className="text-right coupon-amount">
                -$
                {getDiscountAmount(coupon, appliedCoupons).toLocaleString()}
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  );
};

ApplyCoupon.propTypes = {
  onEdit: PropTypes.func,
  variant: PropTypes.oneOf(['mobileTotalSection']), // it indicates the name of its parent component, needed for conditional styling
};

export default withRouter(ApplyCoupon);
